import { useEffect, useState } from "react";
import { onIdTokenChanged, getIdTokenResult, User } from "firebase/auth";
import { auth } from "../services/firebase";
import { Globals } from "../globals";

export interface AuthClaims {
    /** "gemeindeadmin" | "member" | null (the superadmin has no role claim). */
    role: string | null;
    /** The Gemeinde (tenant) the user belongs to, or null if unassigned. */
    gemeindeId: string | null;
    /** Custom claim of the superadmin (GOD-42). */
    superadmin: boolean;
}

/**
 * Subscribes to Firebase auth state and exposes the user together with the
 * multi-tenancy claims (role + gemeindeId) read from the ID token.
 *
 * Roles:
 *  - superadmin: the `superadmin` claim (or, during the transition, the fixed
 *    email), verified email only – full access, all Gemeinden.
 *  - gemeindeadmin: full edit access within their own Gemeinde.
 *  - member: read-only access within their own Gemeinde.
 *  - unassigned: authenticated but no gemeindeId → no data access (holding page).
 *
 * Custom claims are read from the ID token. When an admin changes them, the
 * session fetches a fresh token right away (useClaimsRefresh, GOD-41).
 */
export function useAuth() {
    const [user, setUser] = useState<User | null>(null);
    const [claims, setClaims] = useState<AuthClaims>({ role: null, gemeindeId: null, superadmin: false });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // onIdTokenChanged also fires when the token is refreshed, so new claims
        // (role/gemeindeId changed by an admin, GOD-41) reach every component.
        const unsubscribe = onIdTokenChanged(auth, async (currentUser) => {
            setUser(currentUser);
            if (currentUser) {
                try {
                    const tokenResult = await getIdTokenResult(currentUser);
                    setClaims({
                        role: (tokenResult.claims.role as string | undefined) ?? null,
                        gemeindeId: (tokenResult.claims.gemeindeId as string | undefined) ?? null,
                        superadmin: tokenResult.claims.superadmin === true,
                    });
                } catch {
                    setClaims({ role: null, gemeindeId: null, superadmin: false });
                }
            } else {
                setClaims({ role: null, gemeindeId: null, superadmin: false });
            }
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    // Same rule as firestore.rules and functions/src/authz.ts (GOD-42): the claim,
    // or during the transition the fixed address – both only with a verified email.
    const isSuperAdmin = !!user?.emailVerified && (claims.superadmin || user.email === Globals.ADMIN_EMAIL);
    const isGemeindeadmin = claims.role === Globals.ROLE_GEMEINDEADMIN;
    const isMember = claims.role === Globals.ROLE_MEMBER;

    return {
        user,
        loading,
        isAuthenticated: user !== null,
        role: claims.role,
        gemeindeId: claims.gemeindeId,
        isSuperAdmin,
        isGemeindeadmin,
        isMember,
        /** May edit data in the active Gemeinde (gemeindeadmin or superadmin). */
        canEdit: isSuperAdmin || isGemeindeadmin,
    };
}
